/**
 * /audio-visualizer page controller: sources (microphone / file), the render
 * loop, scenes, fullscreen and screensaver, live recording, URL presets.
 *
 *   analysis  AudioReactiveEngine   public/js/audio-reactive/engine.js (+ core.js, worklet.js)
 *   picture   AVRenderer + AVScenes public/js/audio-visualizer/renderer.js, scenes.js
 *
 * Config comes from the blade as window.__AV = { T: {...strings}, coreUrls,
 * workletUrls }. Why the pieces are the way they are: docs/why/primitives/audio-reactive.md.
 *
 * Invariants worth knowing before editing:
 *   - the microphone is analysed on a 'tap' route and NEVER reaches the
 *     speakers (until 2026-09 a file played first left the analyser wired to
 *     the output, and a later mic went straight into the speakers);
 *   - one AudioContext for the page; a file element can be bound to a context
 *     once, so a replaced context gets a fresh element;
 *   - every new source resets the analysis: a new song brings its own tempo
 *     and levels, and the old hits must not replay on the new clock.
 *
 * ES5 + Promises.
 */
(function () {
    'use strict';

    var C = window.__AV || {};
    var T = C.T || {};
    var SLUG = 'audio-visualizer';
    var FLOW_IDLE = 0.18;
    var NO_SIGNAL_HINT_S = 8;
    var REC_MAX_MS = 30 * 60 * 1000;
    var REC_MAX_MB = 1500;

    function $(id) { return document.getElementById(id); }
    function ae(payload) {
        try { if (window.__ae) window.__ae('feature_use', payload); } catch (e) {}
    }
    function toast(msg, type) {
        try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (e) {}
    }
    function nowS() { return performance.now() / 1000; }

    var wrap = $('avWrap'), canvas = $('avCanvas');
    if (!wrap || !canvas || !window.AVRenderer || !window.AVScenes) return;

    /* ═══════════════ picture ═══════════════ */

    var renderer = window.AVRenderer.create(canvas, {
        onError: function (err) {
            try { if (window.__aeToolError) window.__aeToolError({ error_type: 'av_shader', msg: String(err && err.message || err).slice(0, 300) }); } catch (e) {}
        },
        onLost: function () { ae({ slug: SLUG, f: 'av_context_lost' }); },
        onRestored: function () { ae({ slug: SLUG, f: 'av_context_restored' }); }
    });
    if (!renderer.ok) toast(T.webgl_error, 'error');

    var SCENES = window.AVScenes.list;
    function sceneInfo(id) { for (var i = 0; i < SCENES.length; i++) if (SCENES[i].id === id) return SCENES[i]; return null; }
    function sceneIndex(id) { for (var i = 0; i < SCENES.length; i++) if (SCENES[i].id === id) return i; return 0; }

    /* ═══════════════ state ═══════════════ */

    var ctx = null;
    var engine = null;
    var sourceType = 'mic';
    var sourceNode = null;          /* what the engine and the recorder listen to */
    var mic = null, micStream = null;
    var audioEl = null, fileUrl = null, currentFile = null;
    var running = false;
    var sensitivity = 1;
    var postFx = true;
    var reduceMotion = false;
    try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
    var stats = { switches: 0, fs: 0, saver: 0, rec: 0, startedAt: 0, scene0: null };
    var idle = {
        level: 0, bass: 0, mid: 0, high: 0, kick: 0, snare: 0, hat: 0, onset: 0,
        beat: 0, beatPhase: 0, beatLock: 0, bpm: 0, presence: 0, intensity: 0, bright: 0,
        flow: 0, flowBass: 0, flowHigh: 0, spectrum: new Float32Array(32)
    };

    /* ═══════════════ audio ═══════════════ */

    function makeCtx() {
        var AC = window.AudioContext || window.webkitAudioContext;
        return new AC({ latencyHint: 'interactive' });
    }

    /* A running context; a dead one (device taken, iOS permission sheet) is
       replaced by __liveAudioCtxReady and the current source is rebuilt on it. */
    function liveCtx() {
        if (typeof window.__liveAudioCtxReady === 'function') {
            return window.__liveAudioCtxReady(ctx, makeCtx).then(function (c) {
                if (c !== ctx) { var old = ctx; ctx = c; if (old) onCtxReplaced(); }
                return c;
            });
        }
        if (!ctx) ctx = makeCtx();
        var p = typeof window.__safeResume === 'function' ? window.__safeResume(ctx) : Promise.resolve(true);
        return p.then(function () { return ctx; });
    }

    function onCtxReplaced() {
        detachEngine();
        mic = null;
        if (sourceType === 'mic' && micStream) startMic();
        else if (sourceType === 'file' && currentFile) {
            var at = audioEl ? audioEl.currentTime : 0, wasPlaying = audioEl && !audioEl.paused;
            audioEl = null;
            loadFile(currentFile, at, wasPlaying);
        }
    }

    function detachEngine() {
        if (engine) { try { reportSession(); } catch (e) {} engine.detach(); engine = null; }
        if (sourceNode) { try { sourceNode.disconnect(); } catch (e) {} sourceNode = null; }
        renderer.clearEvents();
    }

    function attachEngine(node, route) {
        detachEngine();
        sourceNode = node;
        engine = window.AudioReactiveEngine.attach(ctx, node, {
            slug: SLUG, route: route, coreUrls: C.coreUrls, workletUrls: C.workletUrls
        });
        engine.src = sourceType;
        engine.setSensitivity(sensitivity);
        engine.ready.then(function (kind) { ae({ slug: SLUG, f: 'av_engine', kind: kind, route: route, sr: ctx.sampleRate }); });
        noSignalSince = nowS();
        noSignalShown = false;
    }

    function stopMic() {
        if (sourceType === 'mic') detachEngine();
        if (mic) { try { mic.stop(); } catch (e) {} }
        micStream = null;
    }

    function startMic() {
        return liveCtx().then(function (c) {
            if (!window.MicManager) throw new Error(T.mic_not_found || 'mic');
            if (!mic) mic = new window.MicManager({ audioContext: c });
            /* no echo cancellation / noise suppression / AGC: they are made for
               speech and eat music (AGC pumps, NS removes sustained notes) */
            return mic.startRaw({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } });
        }).then(function (stream) {
            if (sourceType !== 'mic') { try { stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) {} return; }
            micStream = stream;
            attachEngine(ctx.createMediaStreamSource(stream), 'tap');
        });
    }

    function ensureAudioEl() {
        if (audioEl) return audioEl;
        audioEl = new Audio();
        audioEl.preload = 'auto';
        try { audioEl.playsInline = true; audioEl.setAttribute('playsinline', ''); } catch (e) {}
        /* arm: the first click anywhere lets this element play later, after the
           file picker returns — without it Android refuses play() (no gesture) */
        if (window.__audioGate) window.__audioGate.arm(audioEl);
        audioEl.addEventListener('ended', function () { updatePlayBtn(false); });
        audioEl.addEventListener('pause', function () { updatePlayBtn(false); });
        audioEl.addEventListener('play', function () { updatePlayBtn(true); hidePlayHint(); });
        audioEl.addEventListener('timeupdate', updateFileTime);
        return audioEl;
    }

    function loadFile(file, startAt, autoplay) {
        currentFile = file;
        var el = ensureAudioEl();
        if (fileUrl) { try { URL.revokeObjectURL(fileUrl); } catch (e) {} }
        fileUrl = URL.createObjectURL(file);
        var name = $('avFileName');
        if (name) name.textContent = file.name || '';
        var info = $('avFileInfo');
        if (info) info.classList.add('visible');
        return liveCtx().then(function (c) {
            if (!el.__avNode) el.__avNode = c.createMediaElementSource(el);
            attachEngine(el.__avNode, 'through');
            el.src = fileUrl;
            showExport(true);
            if (startAt) { try { el.currentTime = startAt; } catch (e) {} }
            if (autoplay === false) return;
            return playFile();
        });
    }

    function playFile() {
        if (!audioEl) return Promise.resolve();
        var p = window.__audioGate ? window.__audioGate.play(audioEl) : audioEl.play();
        return Promise.resolve(p).then(function () {
            updatePlayBtn(true);
            if (ctx && typeof window.__safeResume === 'function') window.__safeResume(ctx);
        }, function (err) {
            updatePlayBtn(false);
            if (err && err.name === 'NotAllowedError') { showPlayHint(); ae({ slug: SLUG, f: 'av_play_refused' }); }
            else if (err) toast(T.no_audio, 'warning');
        });
    }

    function showPlayHint() { var h = $('avPlayHint'); if (h) h.hidden = false; var b = $('avFilePlay'); if (b) b.classList.add('av-attn'); }
    function hidePlayHint() { var h = $('avPlayHint'); if (h) h.hidden = true; var b = $('avFilePlay'); if (b) b.classList.remove('av-attn'); }

    function updatePlayBtn(playing) {
        var btn = $('avFilePlay');
        if (!btn) return;
        btn.innerHTML = playing
            ? '<svg aria-hidden="true" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" fill="currentColor"/><rect x="14" y="4" width="4" height="16" fill="currentColor"/></svg>'
            : '<svg aria-hidden="true" viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21" fill="currentColor"/></svg>';
        btn.setAttribute('aria-label', playing ? (T.pause || '') : (T.play || ''));
    }

    function fmtTime(s) {
        if (!isFinite(s)) return '0:00';
        var m = Math.floor(s / 60), sec = Math.floor(s % 60);
        return m + ':' + (sec < 10 ? '0' : '') + sec;
    }

    function updateFileTime() {
        if (!audioEl) return;
        var cur = audioEl.currentTime || 0, dur = audioEl.duration || 0;
        var seek = $('avFileSeek'), tm = $('avFileTime');
        if (seek && dur) seek.value = (cur / dur) * 100;
        if (tm) tm.textContent = fmtTime(cur) + ' / ' + fmtTime(dur);
    }

    /* ═══════════════ render loop ═══════════════ */

    var raf = 0, lastFrame = 0, t0 = nowS();
    var noSignalSince = 0, noSignalShown = false;

    function frame(ms) {
        raf = requestAnimationFrame(frame);
        var now = ms / 1000;
        var dt = lastFrame ? Math.min(0.1, Math.max(0, now - lastFrame)) : 0;
        lastFrame = now;
        var d, events = [], clock;
        if (engine) {
            d = engine.sample();
            events = engine.takeEvents();
            clock = engine.clock();
        } else {
            idle.flow += dt * FLOW_IDLE; idle.flowBass += dt * FLOW_IDLE; idle.flowHigh += dt * FLOW_IDLE;
            d = idle; clock = now;
        }
        renderer.render({ time: now - t0, dt: dt, d: d, events: events, now: clock, motion: reduceMotion ? 0.35 : 1 });
        meters(d);
        watchSignal(d);
    }

    function meters(d) {
        var fill = $('avVolFill');
        if (fill) fill.style.height = Math.round(Math.min(1, d.level * d.presence) * 100) + '%';
        var b = $('avBpmReadout');
        if (b) {
            if (d.bpm > 0 && d.beatLock > 0.5) { b.textContent = Math.round(d.bpm) + ' ' + (T.bpm || 'BPM'); b.classList.add('visible'); }
            else b.classList.remove('visible');
        }
    }

    /* A calm picture on a live microphone is correct in silence — say so once,
       so it does not read as "the visualizer is broken". */
    function watchSignal(d) {
        if (!running || !engine || sourceType !== 'mic' || noSignalShown) return;
        if (d.presence > 0.3) { noSignalSince = nowS(); return; }
        if (nowS() - noSignalSince > NO_SIGNAL_HINT_S) {
            noSignalShown = true;
            toast(T.no_signal || T.no_audio, 'info');
            ae({ slug: SLUG, f: 'av_no_signal' });
        }
    }

    function ensureLoop() { if (!raf) raf = requestAnimationFrame(frame); }

    /* ═══════════════ start / sources ═══════════════ */

    function markRunning() {
        if (running) return;
        running = true;
        stats.startedAt = nowS();
        stats.scene0 = renderer.currentScene();
        $('avStartOverlay').classList.add('hidden');
        if (!isFullscreen) $('avOverlay').classList.remove('hidden');
        showSceneLabel(renderer.currentScene());
        warmNeighbours();
        warmRecorder();
    }

    function startVisualizer() {
        if (running && engine) return;
        if (sourceType === 'mic') {
            startMic().then(function () { markRunning(); }, function (err) {
                /* denied / dismissed / missing / busy: MicManager already shows
                   its recovery dialog; anything else is worth a line */
                if (!err || !err.expected) toast((err && err.message) || T.mic_not_found, 'warning');
            });
        } else if (currentFile) {
            loadFile(currentFile, audioEl ? audioEl.currentTime : 0, true).then(markRunning);
        } else {
            var drop = $('avFileDrop');
            if (drop) { var inp = drop.querySelector('input[type=file]'); if (inp) inp.click(); }
        }
    }

    /* start = false: only switch the chips and stop the previous source (a
       file just picked is loaded by its own handler) */
    function setSource(type, start) {
        if (sourceType === type) return;
        var prev = sourceType;
        sourceType = type;
        document.querySelectorAll('.av-source-chips .av-chip').forEach(function (c) {
            c.classList.toggle('active', c.dataset.source === type);
        });
        var fw = $('avFileWrap');
        if (fw) fw.classList.toggle('visible', type === 'file');
        var icon = $('avOvSourceIcon');
        if (icon) icon.innerHTML = type === 'mic'
            ? '<path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8"/>'
            : '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="12" y2="12"/><line x1="15" y1="15" x2="12" y2="12"/>';
        if (prev === 'mic') stopMic();
        if (prev === 'file') { if (audioEl) audioEl.pause(); detachEngine(); showExport(false); }
        if (!running || start === false) return;
        if (type === 'mic') startMic().catch(function (err) { if (!err || !err.expected) toast((err && err.message) || T.mic_not_found, 'warning'); });
        else if (currentFile) loadFile(currentFile, audioEl ? audioEl.currentTime : 0, true);
        if (type === 'file' && currentFile) showExport(true);
    }

    /* ═══════════════ scenes ═══════════════ */

    function showSceneLabel(id) {
        var info = sceneInfo(id);
        if (!info) return;
        var text = info.icon + ' ' + (T[info.nameKey] || id);
        var label = $('avSceneLabel');
        if (label) {
            label.textContent = text;
            label.classList.remove('fade');
            clearTimeout(label._timer);
            label._timer = setTimeout(function () { label.classList.add('fade'); }, 3000);
        }
        var ov = $('avOvScene');
        if (ov) ov.textContent = text;
    }

    function switchScene(id) {
        if (!sceneInfo(id) || id === renderer.currentScene()) return;
        renderer.setScene(id);
        stats.switches++;
        document.querySelectorAll('.av-scene-card').forEach(function (c) { c.classList.toggle('active', c.dataset.scene === id); });
        showSceneLabel(id);
        writePresetHash();
        warmNeighbours();
    }

    function stepScene(k) {
        var i = sceneIndex(renderer.currentScene());
        switchScene(SCENES[(i + k + SCENES.length) % SCENES.length].id);
    }

    /* compile the scenes the arrows lead to while nothing waits for them */
    function warmNeighbours() {
        var go = function () {
            var i = sceneIndex(renderer.currentScene());
            renderer.warm([SCENES[(i + 1) % SCENES.length].id, SCENES[(i - 1 + SCENES.length) % SCENES.length].id]);
        };
        if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 2000 }); else setTimeout(go, 500);
    }

    /* ═══════════════ fullscreen / screensaver ═══════════════ */

    var isFullscreen = false, screensaverActive = false, screensaverTimer = null, cursorTimer = null, controlsTimer = null;
    var isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    var hasRfs = typeof wrap.requestFullscreen === 'function' || typeof wrap.webkitRequestFullscreen === 'function';
    if (!hasRfs && !isIOS) { var fsb = $('avOvFs'); if (fsb) fsb.style.display = 'none'; }

    function fauxFullscreen() {
        wrap.classList.add('av-faux-fs');
        document.body.style.overflow = 'hidden';
    }

    function enterFullscreen() {
        isFullscreen = true;
        stats.fs++;
        wrap.classList.add('av-fs');
        showOverlay();
        if (isIOS || !hasRfs) fauxFullscreen();
        else if (window.__safeFullscreen) window.__safeFullscreen(wrap, { onDenied: fauxFullscreen, onUnsupported: fauxFullscreen });
        startCursorHide();
    }

    function exitFullscreen() {
        isFullscreen = false;
        wrap.classList.remove('av-fs', 'av-idle', 'av-faux-fs');
        document.body.classList.remove('av-cursor-hidden');
        document.body.style.overflow = '';
        try { if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(function () {}); } catch (e) {}
        clearTimeout(cursorTimer);
        if (screensaverActive) exitScreensaver();
    }

    function toggleFullscreen() { if (isFullscreen) exitFullscreen(); else enterFullscreen(); }

    document.addEventListener('fullscreenchange', function () {
        if (!document.fullscreenElement && isFullscreen && !wrap.classList.contains('av-faux-fs')) exitFullscreen();
    });

    function startCursorHide() {
        clearTimeout(cursorTimer);
        document.body.classList.remove('av-cursor-hidden');
        if (isFullscreen && !screensaverActive) cursorTimer = setTimeout(function () { document.body.classList.add('av-cursor-hidden'); }, 3000);
    }

    function showOverlay() {
        if (screensaverActive) return;
        var ov = $('avOverlay');
        ov.classList.remove('hidden');
        wrap.classList.remove('av-idle');
        clearTimeout(controlsTimer);
        if (isFullscreen) controlsTimer = setTimeout(function () { ov.classList.add('hidden'); wrap.classList.add('av-idle'); }, 5000);
    }

    wrap.addEventListener('mousemove', function () { if (running) { startCursorHide(); showOverlay(); } });
    wrap.addEventListener('touchstart', function () { if (running) showOverlay(); }, { passive: true });

    function enterScreensaver() {
        screensaverActive = true;
        stats.saver++;
        if (!isFullscreen) enterFullscreen();
        $('avOverlay').classList.add('hidden');
        wrap.classList.add('av-saver');
        document.body.classList.add('av-cursor-hidden');
        screensaverTimer = setInterval(function () { stepScene(1); }, 30000);
        $('avOvSaver').classList.add('active');
    }

    function exitScreensaver() {
        screensaverActive = false;
        clearInterval(screensaverTimer);
        screensaverTimer = null;
        wrap.classList.remove('av-saver');
        document.body.classList.remove('av-cursor-hidden');
        $('avOvSaver').classList.remove('active');
        showOverlay();
    }

    function toggleScreensaver() { if (screensaverActive) exitScreensaver(); else enterScreensaver(); }

    /* ═══════════════ sensitivity / post-FX / presets ═══════════════ */

    function fillSlider(el) {
        var min = parseFloat(el.min) || 0, max = parseFloat(el.max) || 100, val = parseFloat(el.value) || 0;
        var pct = ((val - min) / (max - min)) * 100;
        var cs = getComputedStyle(document.documentElement);
        var accent = cs.getPropertyValue('--accent').trim() || '#38bdf8';
        var track = cs.getPropertyValue('--border-hover').trim() || '#444';
        el.style.background = 'linear-gradient(to var(--range-dir,right),' + accent + ' ' + pct + '%,' + track + ' ' + pct + '%)';
    }

    function updateSensitivity(val) {
        sensitivity = Math.max(0.2, Math.min(3, parseFloat(val) || 1));
        var txt = sensitivity.toFixed(1) + 'x';
        ['avSensVal', 'avOvSensVal'].forEach(function (id) { var e = $(id); if (e) e.textContent = txt; });
        ['avSensSlider', 'avOvSens'].forEach(function (id) { var e = $(id); if (e) e.value = sensitivity; });
        var s = $('avSensSlider'); if (s) fillSlider(s);
        if (engine) engine.setSensitivity(sensitivity);
        writePresetHash();
    }

    function setPostFx(on) {
        postFx = !!on;
        renderer.setPostFx(postFx);
        var btn = $('avOvFx');
        if (btn) { btn.classList.toggle('active', postFx); btn.setAttribute('aria-pressed', postFx ? 'true' : 'false'); }
        writePresetHash();
    }

    var presetReady = false;
    function writePresetHash() {
        if (!presetReady) return;
        var p = new URLSearchParams();
        p.set('scene', renderer.currentScene());
        p.set('sens', sensitivity.toFixed(1));
        p.set('fx', postFx ? '1' : '0');
        try { history.replaceState(null, '', '#' + p.toString()); } catch (e) {}
    }

    function readPresetHash() {
        var fxSet = false;
        if (location.hash) {
            var p = new URLSearchParams(location.hash.replace(/^#/, ''));
            var s = p.get('scene');
            if (s && sceneInfo(s)) {
                renderer.setScene(s, true);
                document.querySelectorAll('.av-scene-card').forEach(function (c) { c.classList.toggle('active', c.dataset.scene === s); });
            }
            var sens = parseFloat(p.get('sens'));
            if (!isNaN(sens) && sens >= 0.2 && sens <= 3) updateSensitivity(sens);
            var fx = p.get('fx');
            if (fx === '0' || fx === '1') { setPostFx(fx === '1'); fxSet = true; }
        }
        if (!fxSet) setPostFx(true);
        presetReady = true;
        showSceneLabel(renderer.currentScene());
    }

    /* ═══════════════ live recording ═══════════════ */

    var rec = null;

    function pickMime() {
        var list = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4;codecs=avc1,mp4a.40.2', 'video/mp4'];
        for (var i = 0; i < list.length; i++) {
            try { if (window.MediaRecorder && MediaRecorder.isTypeSupported(list[i])) return list[i]; } catch (e) {}
        }
        return '';
    }

    /* The first video recorder of a Chromium tab drops its first ~2 s of
       picture while the sound runs on. The warm-up starts with the visualizer;
       Record waits for it at most 1.5 s (docs/why/primitives/media-recorder-warm.md). */
    function warmRecorder(maxWaitMs) {
        var mime = pickMime();
        if (!mime || !window.__mediaRecorderWarm) return Promise.resolve();
        return window.__mediaRecorderWarm({ width: canvas.width, height: canvas.height, mimeType: mime, videoBitsPerSecond: 8000000, maxWaitMs: maxWaitMs });
    }

    function fmtRecTime(ms) { var s = Math.floor(ms / 1000); return Math.floor(s / 60) + ':' + ((s % 60) < 10 ? '0' : '') + (s % 60); }

    var recPending = false;
    function startRecording() {
        if (rec || recPending) return;
        if (!running || !engine || !sourceNode) { toast(T.record_start_first || T.click_to_start, 'warning'); return; }
        var mime = pickMime();
        if (!mime || !window.MediaRecorder || !canvas.captureStream) { toast(T.record_unsupported, 'warning'); return; }
        /* one unit per file (a second take of the same song today is free),
           one unit per microphone take */
        var key = sourceType === 'file' && currentFile && window.__toolLimit && window.__toolLimit.fileKey
            ? window.__toolLimit.fileKey(currentFile) : ('live:' + Date.now());
        var cap = window.__toolLimit ? window.__toolLimit.begin(SLUG, key) : null;
        if (window.__toolLimit && !cap) return;
        recPending = true;
        warmRecorder(1500).then(function () {
            recPending = false;
            if (!running || !engine || !sourceNode) { if (cap) cap.abort(); return; }
            beginRecording(mime, cap);
        });
    }

    function beginRecording(mime, cap) {
        var dest = ctx.createMediaStreamDestination();
        sourceNode.connect(dest);
        var tracks = canvas.captureStream(30).getVideoTracks().concat(dest.stream.getAudioTracks());
        var mr;
        try {
            mr = new MediaRecorder(new MediaStream(tracks), { mimeType: mime, videoBitsPerSecond: 8000000, audioBitsPerSecond: 160000 });
        } catch (e) {
            try { sourceNode.disconnect(dest); } catch (x) {}
            if (cap) cap.abort();
            toast(T.record_unsupported, 'warning');
            return;
        }
        var chunks = [], bytes = 0, started = performance.now(), scene = renderer.currentScene();
        rec = { mr: mr, dest: dest, timer: 0, started: started };
        /* the recorded audio carries no output latency: align the picture to
           what the file gets, not to the speakers (docs/why/primitives/audio-reactive.md) */
        if (sourceType === 'file') engine.setAlignment('graph');
        mr.ondataavailable = function (e) {
            if (!e.data || !e.data.size) return;
            chunks.push(e.data);
            bytes += e.data.size;
            if (bytes > REC_MAX_MB * 1024 * 1024) { stopRecording(); toast(T.record_max_size, 'info'); }
        };
        mr.onstop = function () {
            var durSec = (performance.now() - started) / 1000;
            var type = (mr.mimeType || mime).split(';')[0] || 'video/webm';
            var ext = type.indexOf('mp4') >= 0 ? 'mp4' : 'webm';
            var blob = new Blob(chunks, { type: type });
            chunks = [];
            var fix = ext === 'webm' && window.WebmFixDuration ? window.WebmFixDuration.patch(blob, durSec) : Promise.resolve(blob);
            fix.catch(function () { return blob; }).then(function (out) {
                if (!out || !out.size) {
                    if (cap) cap.abort();
                    ae({ slug: SLUG, f: 'record_empty', ext: ext, sec: Math.round(durSec), src: sourceType });
                    return null;
                }
                var stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
                var name = 'visualizer-' + scene + '-' + stamp + '.' + ext;
                var dl = window.__downloadFile ? window.__downloadFile(out, name) : null;
                if (cap) cap.commit();
                ae({ slug: SLUG, f: 'record_saved', ext: ext, sec: Math.round(durSec), mb: Math.round(out.size / 1048576), src: sourceType });
                return dl;
            });
        };
        mr.start(1000);
        stats.rec++;
        $('avOvRec').classList.add('active'); $('avOvRec').setAttribute('aria-pressed', 'true');
        $('avRecLed').classList.add('visible');
        wrap.classList.add('av-rec');
        rec.timer = setInterval(function () {
            var ms = performance.now() - started;
            var rt = $('avRecTime'); if (rt) rt.textContent = fmtRecTime(ms);
            if (ms >= REC_MAX_MS) stopRecording();
        }, 250);
        toast(T.record_started || T.record, 'info');
        ae({ slug: SLUG, f: 'record_started', src: sourceType, mime: mime });
    }

    function stopRecording() {
        if (!rec) return;
        var r = rec;
        rec = null;
        clearInterval(r.timer);
        try { r.mr.stop(); } catch (e) {}
        try { sourceNode && sourceNode.disconnect(r.dest); } catch (e) {}
        if (engine) engine.setAlignment('audible');
        $('avOvRec').classList.remove('active'); $('avOvRec').setAttribute('aria-pressed', 'false');
        $('avRecLed').classList.remove('visible');
        wrap.classList.remove('av-rec');
        ae({ slug: SLUG, f: 'record_stopped', durMs: Math.round(performance.now() - r.started) });
    }

    function toggleRecording() { if (rec) stopRecording(); else startRecording(); }

    /* ═══════════════ a video of the whole song (offline) ═══════════════ */

    /* The song as a video file: every frame rendered from the same analysis at a
       fixed rate and encoded with the song's audio (export.js). Live recording
       stays for the microphone and for "record while I play". */
    var exporting = null;

    function showExport(on) {
        var box = $('avExport');
        if (box) box.hidden = !on || !window.AVExport || typeof window.VideoEncoder !== 'function';
    }

    function exportSize() {
        var s = parseInt(($('avExSize') || {}).value, 10) || 720;
        var a = ($('avExAspect') || {}).value || '16:9';
        var even = function (v) { return Math.round(v / 2) * 2; };
        if (a === '9:16') return { w: s, h: even(s * 16 / 9), aspect: a };
        if (a === '1:1') return { w: s, h: s, aspect: a };
        return { w: even(s * 16 / 9), h: s, aspect: a };
    }

    function fmtLeft(sec) {
        sec = Math.max(0, Math.round(sec));
        return Math.floor(sec / 60) + ':' + (sec % 60 < 10 ? '0' : '') + (sec % 60);
    }

    function exportUi(state, frac, text) {
        var btn = $('avExGo'), bar = $('avExProgress'), fill = $('avExFill'), status = $('avExStatus');
        if (btn) btn.disabled = state === 'busy';
        if (bar) bar.hidden = state === 'idle';
        if (fill) fill.style.width = Math.round(Math.max(0, Math.min(1, frac || 0)) * 100) + '%';
        if (status && text != null) status.textContent = text;
    }

    function startExport() {
        if (exporting || !currentFile || !window.AVExport) return;
        var size = exportSize();
        var fmt = (($('avExFormat') || {}).value || 'mp4') === 'webm' ? 'webm' : 'mp4';
        var key = window.__toolLimit && window.__toolLimit.fileKey ? window.__toolLimit.fileKey(currentFile) : null;
        var cap = window.__toolLimit ? window.__toolLimit.begin(SLUG, key) : null;
        if (window.__toolLimit && !cap) return;
        var ac = new AbortController();
        var file = currentFile, scene = renderer.currentScene(), t0 = performance.now();
        exporting = { ac: ac };
        if (audioEl && !audioEl.paused) audioEl.pause();
        /* the page stops drawing while the export renders: one GPU, one job */
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
        var ETA = window.__acETA;
        exportUi('busy', 0, T.export_decoding);
        ae({ slug: SLUG, f: 'av_export_start', w: size.w, h: size.h, aspect: size.aspect, fmt: fmt, scene: scene });
        /* 'remux' when the sound is glued on after the picture (export.js) */
        var lane = 'direct';
        liveCtx().then(function (c) {
            return window.AVExport.run({
                file: file, ctx: c, scene: scene, postFx: postFx, sensitivity: sensitivity,
                motion: reduceMotion ? 0.35 : 1, width: size.w, height: size.h, fps: 30, format: fmt,
                signal: ac.signal,
                onStage: function (st) {
                    if (st === 'render') { if (ETA) ETA.beginStage('av-export'); exportUi('busy', 0, T.export_rendering); }
                    if (st === 'sound') { lane = 'remux'; if (ETA) ETA.endStage('av-export'); exportUi('busy', 1, T.export_sound); }
                },
                onProgress: function (f) {
                    var left = null;
                    if (ETA) { ETA.updateRate('av-export', f); left = ETA.remainingSec('av-export'); }
                    exportUi('busy', f, (T.export_rendering || '') + ' ' + Math.round(f * 100) + '%' +
                        (left != null && isFinite(left) ? ' · ' + String(T.export_eta || '').replace(':time', fmtLeft(left)) : ''));
                }
            });
        }).then(function (blob) {
            /* the shared length gate refused the file: its dialog has spoken */
            if (!blob) { if (cap) cap.abort(); exportUi('idle', 0, ''); ae({ slug: SLUG, f: 'av_export_refused' }); return; }
            var ext = fmt;
            var base = String(file.name || 'song').replace(/\.[^.]+$/, '');
            var info = sceneInfo(scene);
            var sceneName = info ? (T[info.nameKey] || scene) : scene;
            var name = window.__outputName ? window.__outputName.join([base, sceneName], ' - ', ext) : (base + ' - ' + sceneName + '.' + ext);
            if (window.__downloadFile) window.__downloadFile(blob, name);
            if (cap) cap.commit();
            exportUi('idle', 1, '');
            toast(T.export_done, 'success');
            ae({ slug: SLUG, f: 'av_export_done', w: size.w, h: size.h, fmt: fmt, scene: scene, lane: lane, mb: Math.round(blob.size / 1048576), ms: Math.round(performance.now() - t0) });
        }, function (err) {
            var canceled = window.__isUserCancel ? window.__isUserCancel(err, ac.signal) : ac.signal.aborted;
            if (cap) cap.abort();
            exportUi('idle', 0, '');
            if (canceled) { toast(T.export_canceled, 'info'); ae({ slug: SLUG, f: 'av_export_cancel', ms: Math.round(performance.now() - t0) }); return; }
            /* too long for this device: the shared dialog has already said so */
            if (err && err.name === 'MemoryBudgetError' && err.userInformed) return;
            var tooLong = err && (err.name === 'MemoryBudgetError' || err.code === 'too_long');
            var msg = tooLong ? T.export_too_long : (err && err.code === 'unsupported' ? T.export_unsupported : T.export_failed);
            toast(msg, 'error');
            ae({ slug: SLUG, f: 'av_export_fail', lane: lane, reason: String(err && (err.code || err.message) || err).slice(0, 120) });
        }).then(function () {
            exporting = null;
            if (ETA) ETA.endStage('av-export');
            ensureLoop();
        });
    }

    /* ═══════════════ telemetry ═══════════════ */

    var sessionSent = false;
    function reportSession() {
        if (!engine || !stats.startedAt) return;
        var s = engine.summary();
        s.slug = SLUG; s.f = 'av_session'; s.src = engine.src;
        s.scene = renderer.currentScene(); s.scene0 = stats.scene0; s.switches = stats.switches;
        s.fx = postFx ? 1 : 0; s.sens = sensitivity; s.fs = stats.fs; s.saver = stats.saver; s.rec = stats.rec;
        s.rm = reduceMotion ? 1 : 0;
        ae(s);
    }
    window.addEventListener('pagehide', function () {
        if (rec) try { rec.mr.stop(); } catch (e) {}
        if (!sessionSent) { sessionSent = true; try { reportSession(); } catch (e) {} }
    });

    /* ═══════════════ wiring ═══════════════ */

    $('avStartOverlay').addEventListener('click', startVisualizer);
    document.querySelectorAll('.av-scene-card').forEach(function (card) {
        card.addEventListener('click', function () { switchScene(this.dataset.scene); });
    });
    document.querySelectorAll('.av-source-chips .av-chip').forEach(function (chip) {
        chip.addEventListener('click', function () { setSource(this.dataset.source); });
    });
    var drop = $('avFileDrop');
    // Race-safe (tool-uploader.js __bindUploaderFile): a file picked before this
    // deferred module attached is replayed instead of going to nobody.
    var onPickedFile = function (file) {
        if (!file) return;
        if (!/^(audio|video)\//.test(file.type || '') && !/\.(mp3|wav|ogg|oga|flac|m4a|aac|opus|webm|mp4|mov|mkv)$/i.test(file.name || '')) return;
        if (sourceType !== 'file') setSource('file', false);
        loadFile(file, 0, true).then(markRunning, function () { toast(T.no_audio, 'warning'); });
    };
    if (drop) {
        if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(drop, onPickedFile);
        else drop.addEventListener('tool-uploader:file', function (e) { onPickedFile(e.detail && e.detail.file); });
    }
    drop && drop.addEventListener('tool-uploader:clear', function () {
        if (audioEl) audioEl.pause();
        if (exporting) exporting.ac.abort();
        showExport(false);
        currentFile = null;
        detachEngine();
        var info = $('avFileInfo'); if (info) info.classList.remove('visible');
        hidePlayHint();
    });
    $('avFilePlay').addEventListener('click', function () {
        if (!audioEl) return;
        if (audioEl.paused) playFile(); else audioEl.pause();
    });
    $('avFileSeek').addEventListener('input', function () {
        if (audioEl && audioEl.duration) audioEl.currentTime = (this.value / 100) * audioEl.duration;
    });
    $('avSensSlider').addEventListener('input', function () { updateSensitivity(this.value); });
    $('avOvSens').addEventListener('input', function () { updateSensitivity(this.value); });
    $('avOvPrev').addEventListener('click', function () { stepScene(-1); });
    $('avOvNext').addEventListener('click', function () { stepScene(1); });
    $('avOvFs').addEventListener('click', toggleFullscreen);
    $('avOvSaver').addEventListener('click', toggleScreensaver);
    $('avOvSource').addEventListener('click', function () { setSource(sourceType === 'mic' ? 'file' : 'mic'); });
    $('avOvFx').addEventListener('click', function () { setPostFx(!postFx); });
    $('avOvRec').addEventListener('click', toggleRecording);
    var exGo = $('avExGo'); if (exGo) exGo.addEventListener('click', startExport);
    var exCancel = $('avExCancel'); if (exCancel) exCancel.addEventListener('click', function () { if (exporting) exporting.ac.abort(); });
    $('avShortcutsToggle').addEventListener('click', function () {
        this.classList.toggle('open');
        $('avShortcutsList').classList.toggle('visible');
    });

    document.addEventListener('keydown', function (e) {
        var tag = e.target && e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target && e.target.isContentEditable)) return;
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        switch ((e.key || '').toLowerCase()) {
            case 'f': e.preventDefault(); toggleFullscreen(); break;
            case 's': if (!running) return; e.preventDefault(); toggleScreensaver(); break;
            case ' ':
                e.preventDefault();
                if (!running) startVisualizer(); else setSource(sourceType === 'mic' ? 'file' : 'mic');
                break;
            case 'arrowleft': if (running) stepScene(-1); break;
            case 'arrowright': if (running) stepScene(1); break;
            case 'escape': if (screensaverActive) { exitScreensaver(); exitFullscreen(); } else if (isFullscreen) exitFullscreen(); break;
            case '+': case '=': updateSensitivity(sensitivity + 0.1); break;
            case '-': updateSensitivity(sensitivity - 0.1); break;
            case 'p': e.preventDefault(); setPostFx(!postFx); break;
            case 'r': if (!running) return; e.preventDefault(); toggleRecording(); break;
        }
    });

    var touchX = 0, lastTap = 0;
    canvas.addEventListener('touchstart', function (e) { touchX = e.touches[0].clientX; }, { passive: true });
    canvas.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - touchX;
        if (Math.abs(dx) > 50) { stepScene(dx > 0 ? -1 : 1); return; }
        var t = Date.now();
        if (t - lastTap < 300) toggleFullscreen();
        lastTap = t;
    });
    canvas.addEventListener('click', function () { if (screensaverActive) { exitScreensaver(); exitFullscreen(); } });

    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(function () { renderer.resize(); }).observe(canvas);
    try {
        window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', function (ev) { reduceMotion = ev.matches; });
    } catch (e) {}
    document.querySelectorAll('.av-slider').forEach(fillSlider);

    ensureAudioEl();
    readPresetHash();
    ensureLoop();

    /* test seam (tests/playwright/audio-visualizer-*.mjs): read-only state */
    window.__AVApp = {
        state: function () {
            var d = engine ? engine.tracker.d : idle;
            return {
                running: running, source: sourceType, kind: engine ? engine.kind : null,
                scene: renderer.currentScene(), recording: !!rec, sensitivity: sensitivity,
                level: d.level, bass: d.bass, presence: d.presence, bpm: d.bpm, beatLock: d.beatLock,
                playing: !!(audioEl && !audioEl.paused),
                inDb: engine ? engine.tracker.groupSeries[3].sm : null
            };
        }
    };
})();
